// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { REFRESH_PERMISSIONS } from '@/permissions/constants';
import DashboardFilterRef from '@/util/DashboardFilterRef';
import { equals } from 'ramda';
import { updateArrayElement } from '@/util/helpers/array-helpers';
import { PREVIEW, TEXTBOX } from '@/components/ChartHolder.constants';
import {
  CHANGE_TAB,
  DASHBOARD_CONTENT_LOADED,
  DASHBOARD_CREATED,
  DASHBOARD_DELETED,
  DASHBOARD_FILTERS_LOADED,
  DASHBOARD_LOADED,
  DASHBOARD_REFRESHED,
  DASHBOARD_UPDATED,
  DASHBOARDS_LOADED,
  FINISHED,
  LOADING,
  MODAL_INTERACTION,
  PIN_REFRESHED,
  REMOVE_PREVIEW_WIDGET,
  SELECT_SAVED_QUERY_FOR_WIDGET,
  WIDGET_ADDED,
  WIDGET_CONFIGURATION_UPDATED,
  WIDGET_FILTERS_UPDATED,
} from '../../actions/constants';
import { buildDataTable } from '../rows';
import ViewRef from '../../util/ViewRef';
import { DASHBOARDS_VIEW } from '../../util/enums/EnumModals';
import { DASHBOARDS } from '../../util/enums/views.constants';
import DashboardRef from '../../util/DashboardRef';
import { DashBoardsState } from './types';
import { TOTAL_COLUMNS, UNIT } from './consts';
import { isOccupied } from './helpers';
import { LEFT } from '../../util/TextboxRef';
import DashboardChartWidget from '../../util/DashboardChartWidget';
import DashboardTextBoxWidget from '../../util/DashboardTextboxWidget';
import {
  RESET_APPLIED_FILTERS,
  RESET_DASHBOARDS_STATE,
  RESET_PREVIEW_WIDGET,
} from './actions';

export const initialState: DashBoardsState = {
  dashboards: null,
  active: null,
  chartWidgets: null, // list of Dashboard chart Widget objs
  textWidgets: null, // list of Dashboard texbox Widget objs
  previewWidget: null,
  outstandingWidgetRequests: 0,
  views: null,
  dataTables: [],
  errors: [],
  chartConfigs: [],
  unsavedChanges: false,
  saveOnBoardingNeeded: false,
  refreshId: null,
  lastRefreshed: null,
  viewsAwaitingResults: [],
  secondsBetweenRetry: 1,
  filters: [],
  appliedFilters: [],
  textBoxesInEditMode: [],
  isSnapshot: false,
  publicId: null,
  publicPin: null,
  public: false,
};

const toNull = () => null;

const getWidgetByKey = (widgets, key) =>
  widgets && widgets.find((w) => parseInt(w.key, 10) === parseInt(key, 10));

function backToDashboards(state) {
  if (!state.unsavedChanges || window.confirm('Discard unsaved changes?')) {
    return {
      ...initialState, // reset to initial state
      dashboards: state.dashboards, // but keep list of dashboards
    };
  }
  return state;
}

function interaction(state, payload) {
  switch (payload.section) {
    case 'layout': {
      if (state.previewWidget) return state;

      const chartWidgets = payload.layout
        .filter((w) => {
          const widget = getWidgetByKey(state.chartWidgets, w.i);
          return widget && widget.displayType !== TEXTBOX;
        })
        .map((w) => {
          const widget = getWidgetByKey(state.chartWidgets, w.i);
          const display = widget.displayType;
          return DashboardChartWidget.create(
            widget.series,
            w.i,
            w.x,
            w.y,
            w.w,
            w.h,
            display,
            widget.widgetTitle,
            widget.chartSetting,
            widget.metricColumns,
            widget.dimensionColumns,
          );
        });
      const textWidgets = payload.layout
        .filter((w) => {
          const widget = getWidgetByKey(state.textWidgets, w.i);
          return widget && widget.displayType === TEXTBOX;
        })

        .map((w) => {
          const widget = getWidgetByKey(state.textWidgets, w.i);
          return DashboardTextBoxWidget.create(
            widget.value,
            widget.textAlign,
            w.i,
            w.x,
            w.y,
            w.w,
            w.h,
          );
        });

      const changed =
        state.unsavedChanges ||
        chartWidgets.length !== state.chartWidgets.length ||
        textWidgets.length !== state.textWidgets.length ||
        state.chartWidgets.some((w, index) => !chartWidgets[index].equals(w)) ||
        state.textWidgets.some((w, index) => !textWidgets[index].equals(w));

      return {
        ...state,
        chartWidgets,
        textWidgets,
        unsavedChanges: changed,
      };
    }
    case 'toggle-public': {
      return { ...state, public: !state.public };
    }
    case 'change-display-type': {
      // get existing widget
      const w = getWidgetByKey(state.chartWidgets, payload.widgetIndex);
      // create a new widget from the existing one, changing the display type
      const newWidget = DashboardChartWidget.create(
        w.series,
        w.key,
        w.x,
        w.y,
        w.width,
        w.height,
        payload.newDisplayType,
        w.widgetTitle,
        w.chartSetting,
      );
      // update array of widgets with new one
      const index = state.chartWidgets.findIndex(
        (e) => e.key === payload.widgetIndex,
      );

      const newWidgets = updateArrayElement(
        state.chartWidgets,
        index,
        newWidget,
      );

      // return new state object
      return { ...state, chartWidgets: newWidgets, unsavedChanges: true };
    }
    case 'back': {
      return backToDashboards(state);
    }
    case 'deleteWidget': {
      let textWidgets = state.textWidgets;
      let chartWidgets = state.chartWidgets;
      let views = state.views;
      let dataTables = state.dataTables;
      let errors = state.errors;
      let chartConfigs = state.chartConfigs;
      let textBoxesInEditMode = state.textBoxesInEditMode;

      // To delete a widget first we find the index in the array where it is depending on the type

      const widgetToRemove =
        payload.displayType === TEXTBOX
          ? state.textWidgets.findIndex((e) => e.key === payload.widgetIndex)
          : state.chartWidgets.findIndex((e) => e.key === payload.widgetIndex);

      // We remove the widget from the array where it is

      if (payload.displayType === TEXTBOX) {
        textWidgets = [
          ...state.textWidgets.slice(0, widgetToRemove),
          ...state.textWidgets.slice(widgetToRemove + 1),
        ];

        textBoxesInEditMode = [
          ...state.textBoxesInEditMode.slice(0, widgetToRemove),
          ...state.textBoxesInEditMode.slice(widgetToRemove + 1),
        ];
      } else {
        chartWidgets = [
          ...state.chartWidgets.slice(0, widgetToRemove),
          ...state.chartWidgets.slice(widgetToRemove + 1),
        ];

        // For chart widgets only we remove the view, chart config, data tables and errors

        views = [
          ...state.views.slice(0, widgetToRemove),
          ...state.views.slice(widgetToRemove + 1),
        ];

        dataTables = [
          ...state.dataTables.slice(0, widgetToRemove),
          ...state.dataTables.slice(widgetToRemove + 1),
        ];

        errors = [
          ...state.errors.slice(0, widgetToRemove),
          ...state.errors.slice(widgetToRemove + 1),
        ];

        chartConfigs = [
          ...state.chartConfigs.slice(0, widgetToRemove),
          ...state.chartConfigs.slice(widgetToRemove + 1),
        ];
      }

      return {
        ...state,
        views,
        chartWidgets,
        textWidgets,
        dataTables,
        errors,
        chartConfigs,
        textBoxesInEditMode,
        unsavedChanges: true,
      };
    }

    case 'update-textbox': {
      const w = getWidgetByKey(state.textWidgets, payload.widgetIndex);

      const index =
        state.textWidgets &&
        state.textWidgets.findIndex((e) => e.key === payload.widgetIndex);

      if (index !== null && w !== null && w.hasChangesMade(payload.newRef)) {
        // Don't update the widget if textboxRef hasn't changed so the dashboard doesn't think a change was done
        // create a new widget from the existing one, changing the display type
        const newWidget = DashboardTextBoxWidget.create(
          payload.newRef.value,
          payload.newRef.textAlign,
          w.key,
          w.x,
          w.y,
          w.width,
          w.height,
        );

        // update array of widgets with new one

        const newWidgets = updateArrayElement(
          state.textWidgets,
          index,
          newWidget,
        );

        // return new state object
        return { ...state, textWidgets: newWidgets, unsavedChanges: true };
      }

      return state;
    }

    case 'toggle-textbox-edit-mode': {
      return {
        ...state,
        textBoxesInEditMode: updateArrayElement(
          state.textBoxesInEditMode,
          payload.widgetIndex,
          payload.value,
        ),
      };
    }

    case 'applied-filters': {
      return { ...state, appliedFilters: payload.filters };
    }
    default:
      return state;
  }
}

export const dashboardsReducer = (state = initialState, action) => {
  switch (action.type) {
    case RESET_DASHBOARDS_STATE:
      return initialState;
    case FINISHED: {
      if (action.awaiting === DASHBOARD_CONTENT_LOADED) {
        return {
          ...state,
          outstandingWidgetRequests: state.outstandingWidgetRequests - 1, // decrement
        };
      }
      return state;
    }
    case LOADING: {
      if (action.awaiting === DASHBOARD_LOADED) {
        return {
          ...state,
          active: action.meta.dashboard,
          chartWidgets: null, // clear out widgets, as they're about to be populated
          textWidgets: null,
        };
      }
      if (action.awaiting === DASHBOARD_CONTENT_LOADED) {
        // identify the widgets that are being refreshed
        const { views: metaViews = [] } = action.meta;
        const indexesBeingRefreshed = (state.views ?? []).reduce(
          (refreshedIndexes, view, i) => {
            if (metaViews.some((mView) => mView.series() === view.series())) {
              refreshedIndexes.push(i);
            }

            return refreshedIndexes;
          },
          [],
        );
        // and set them to null so the loading icon shows up (NOTE: have commented out part of this logic, see comment below)
        const newDataTables = [...state.dataTables];
        const newErrors = [...state.errors];
        indexesBeingRefreshed.forEach((index) => {
          if (action.meta.showLoaderImmediately) {
            // we only clear this if explicitly requested, as we don't want the dashboards to show a loading icon every 5 minutes when they refresh themselves (see Dashboards.componentDidMount)
            newDataTables[index] = null;
          }
          newErrors[index] = null;
        });
        return {
          ...state,
          dataTables: newDataTables,
          errors: newErrors,
          outstandingWidgetRequests: state.outstandingWidgetRequests + 1, // increment
        };
      }
      return state;
    }
    case DASHBOARD_REFRESHED: {
      return {
        ...state,
        refreshId: action.payload.json._refreshId,
        lastRefreshed: action.payload.json._lastRefreshed,
        viewsAwaitingResults: action.payload.views.filter(Boolean), // remove nulls (text boxes)
      };
    }
    case DASHBOARD_FILTERS_LOADED: {
      return {
        ...state,
        filters: action.payload.filters,
        appliedFilters: action.payload.appliedFilters,
        filtersLoaded: true,
      };
    }
    case DASHBOARD_LOADED: {
      const views = action.payload.json._views.map(ViewRef.build);

      const chartWidgets = action.payload.json._widgets
        .filter((w) => w._displayType !== TEXTBOX)
        .map(DashboardChartWidget.fromJson);

      const textWidgets = action.payload.json._widgets
        .filter((w) => w._displayType === TEXTBOX)
        .map(DashboardTextBoxWidget.fromJson);

      return {
        ...state,
        refreshId: action.payload.json._latestRefreshId,
        lastRefreshed: action.payload.json._lastRefreshed,
        publicId: action.payload.json._publicId,
        publicPin: action.payload.json._publicPin,
        public: action.payload.json._isPublic,
        active: action.payload.dashboard,
        chartWidgets,
        textWidgets,
        views,
        viewsAwaitingResults: views.filter(Boolean), // remove nulls (text boxes)
        dataTables: views.map(toNull),
        errors: views.map(toNull),
        unsavedChanges: false,
        saveOnBoardingNeeded: views.length === 0,
        appliedFilters: [], // reset
        textBoxesInEditMode: textWidgets.map(() => false), // set all to false
      };
    }

    case CHANGE_TAB: {
      if (action.payload.tab !== DASHBOARDS) {
        return {
          ...state, // reset (to clear up memory)
          dataTables: state.dataTables.map(toNull),
          errors: state.errors.map(toNull),
          viewsAwaitingResults: state.views
            ? state.views.filter(Boolean) // remove nulls (text boxes)
            : [], // prepare to reload the results when dashboard is opened again.
        };
      }
      if (action.payload.tab === action.payload.currentTab) {
        // user clicked dashboards tab when on a dashboard, so take them back to dashboards list
        return backToDashboards(state);
      }
      return state;
    }
    case WIDGET_ADDED: {
      const nextChartKey =
        !state.chartWidgets || state.chartWidgets.length === 0
          ? 0
          : Math.max(...state.chartWidgets.map((w) => w.key)) + 1;

      const nextTextKey =
        !state.textWidgets || state.textWidgets.length === 0
          ? 0
          : Math.max(...state.textWidgets.map((w) => w.key)) + 1;

      const key = nextChartKey + nextTextKey;
      const isPreview = action.payload.display === PREVIEW;

      let widget = null;
      for (let row = 0; !widget; row++) {
        for (
          let column = 0;
          column < TOTAL_COLUMNS;
          column += TOTAL_COLUMNS / 4
        ) {
          const WIDTH = 4 * UNIT;
          const HEIGHT = 4 * UNIT;
          if (
            !isOccupied(column, row, state.textWidgets, WIDTH, HEIGHT) &&
            !isOccupied(column, row, state.chartWidgets, WIDTH, HEIGHT)
          ) {
            widget =
              action.payload.display === TEXTBOX
                ? DashboardTextBoxWidget.create(
                    null,
                    LEFT,
                    key,
                    column,
                    row,
                    WIDTH,
                    HEIGHT,
                  )
                : DashboardChartWidget.create(
                    isPreview ? null : action.payload.view.series(),
                    key,
                    column,
                    row,
                    WIDTH,
                    HEIGHT,
                    action.payload.display,
                  ).setIsPreview(isPreview);
            break;
          }
        }
      }

      let textWidgets = state.textWidgets;
      let chartWidgets = state.chartWidgets;
      let views = state.views;
      let dataTables = state.dataTables;
      let chartConfigs = state.chartConfigs;
      let errors = state.errors;
      let textBoxesInEditMode = state.textBoxesInEditMode;
      let viewsAwaitingResults = state.viewsAwaitingResults;
      const isInEditMode = action.payload.display === TEXTBOX;

      if (action.payload.display === TEXTBOX) {
        textWidgets = [...(state.textWidgets ?? []), widget];
        textBoxesInEditMode = [...state.textBoxesInEditMode, isInEditMode];
      } else if (!isPreview) {
        chartWidgets = [...state.chartWidgets, widget];
        views = [...state.views, action.payload.view];
        dataTables = [...state.dataTables, null];
        chartConfigs = [...state.chartConfigs, null];
        errors = [...state.errors, null];
        viewsAwaitingResults = [
          ...state.viewsAwaitingResults,
          action.payload.view,
        ];
      }

      return {
        ...state,
        previewWidget: isPreview ? widget : null,
        textWidgets,
        chartWidgets,
        views,
        viewsAwaitingResults,
        dataTables,
        errors,
        chartConfigs,
        unsavedChanges: !isPreview,
        textBoxesInEditMode,
      };
    }

    case REMOVE_PREVIEW_WIDGET: {
      return { ...state, previewWidget: null };
    }

    case 'UPDATE_WIDGET_CHART_CONFIGS': {
      const { widgetKey, chartType, metrics, dimensions } = action.payload;
      const widget = getWidgetByKey(state.chartWidgets, widgetKey);

      const newWidget = DashboardChartWidget.create(
        widget.series,
        widget.key,
        widget.x,
        widget.y,
        widget.width,
        widget.height,
        chartType,
        widget.widgetTitle,
        widget.chartSetting,
        metrics,
        dimensions,
      );

      // update array of widgets with new one
      const index = state.chartWidgets.findIndex((e) => e.key === widgetKey);

      const newWidgets = updateArrayElement(
        state.chartWidgets,
        index,
        newWidget,
      );

      // return new state object
      return {
        ...state,
        chartWidgets: newWidgets,
        unsavedChanges: state.unsavedChanges || !equals(widget, newWidget),
      };
    }

    case WIDGET_FILTERS_UPDATED: {
      return { ...state, unsavedChanges: true };
    }

    case SELECT_SAVED_QUERY_FOR_WIDGET: {
      const index = action.payload.widgetIndex;
      const dataTables = [...state.dataTables];
      const chartConfigs = [...state.chartConfigs];
      const views = [...state.views];
      const chartWidgets = [...state.chartWidgets];
      if (!state.previewWidget && (index || index === 0)) {
        const currentWidget = chartWidgets[index];
        dataTables[index] = null;
        chartConfigs[index] = null;
        views[index] = action.payload.view;
        chartWidgets[index] = DashboardChartWidget.create(
          action.payload.view.series(),
          currentWidget.key,
          currentWidget.x,
          currentWidget.y,
          currentWidget.width,
          currentWidget.height,
          'table',
        );
      } else {
        dataTables.push(null);
        chartConfigs.push(null);
        views.push(action.payload.view);
        chartWidgets.push(
          DashboardChartWidget.create(
            action.payload.view.series(),
            state.previewWidget.key,
            state.previewWidget.x,
            state.previewWidget.y,
            state.previewWidget.width,
            state.previewWidget.height,
            'table',
          ),
        );
      }
      return {
        ...state,
        viewsAwaitingResults: [
          ...state.viewsAwaitingResults,
          action.payload.view,
        ],
        previewWidget: null,
        unsavedChanges: true,
        dataTables,
        chartConfigs,
        views,
        chartWidgets,
      };
    }

    case DASHBOARD_CREATED: {
      // hack
      setTimeout(() => window.dispatchEvent(new Event(REFRESH_PERMISSIONS)), 0);
      if (action.payload.existing) {
        // update existing
        const index = state.dashboards.findIndex(
          (d) => d.id() === action.payload.existing.id(),
        );
        return {
          ...state,
          dashboards: [
            ...state.dashboards.slice(0, index),
            DashboardRef.build(action.payload.json),
            ...state.dashboards.slice(index + 1),
          ],
          active: state.active
            ? DashboardRef.build(action.payload.json)
            : state.active,
        };
      }
      // added new
      return {
        ...state,
        dashboards: [
          DashboardRef.build(action.payload.json),
          ...state.dashboards,
        ],
      };
    }
    case RESET_PREVIEW_WIDGET: {
      return { ...state, previewWidget: null };
    }
    case DASHBOARD_DELETED: {
      return {
        ...state,
        dashboards: state.dashboards.filter(
          (d) => d.id() !== action.payload.deleted.id(),
        ),
      };
    }
    case DASHBOARD_UPDATED: {
      // save successful
      return { ...state, unsavedChanges: false, saveOnBoardingNeeded: false };
    }
    case RESET_APPLIED_FILTERS: {
      if (state.appliedFilters.length) {
        return {
          ...state,
          appliedFilters: [],
          refreshId: null,
          lastRefreshed: null,
        };
      }
      return state;
    }
    case DASHBOARDS_LOADED: {
      const active = state.active ? state.active.resetRefreshId() : null;
      const activeDashboard = active
        ? action.payload
            .map(DashboardRef.buildNew)
            .find((d) => d.id() === state.active.id())
        : active;
      return {
        ...state,
        dashboards: action.payload.map(DashboardRef.buildNew),
        active: activeDashboard,
      };
    }

    case 'UPDATE_DASHBOARD_FILTERS': {
      return {
        ...state,
        filters: state.filters.map((filter) => {
          const updateFilter = action.payload.find(
            (f) => f.filterId === filter._id,
          );

          if (!updateFilter) return filter;

          return new DashboardFilterRef(
            filter._id,
            filter._name,
            filter._columnType,
            Object.keys(updateFilter.columnsBySeries),
            filter._presets,
          );
        }),
      };
    }

    case DASHBOARD_CONTENT_LOADED: {
      if (!state.views) {
        // too late, user has already navigated away
        return state;
      }
      const viewsAwaitingResults = [...state.viewsAwaitingResults]; // take copy
      const viewsWithResults = [];
      const loadedViews = action.payload.json._views.map(ViewRef.build);
      const widgetContents = action.payload.json._widgetContents.map(
        (wc, index) => {
          const view = loadedViews[index];
          if (wc._status === 'running') {
            if (
              !viewsAwaitingResults.find((v) => v.series() === view.series())
            ) {
              // don't add duplicates
              viewsAwaitingResults.push(view);
            }
          } else {
            viewsWithResults.push(view);
          }
          return {
            dt: wc._status !== 'success' ? null : buildDataTable(wc._dataTable),
            chartConfig: wc._chartConfig,
            error: wc._errorMsg,
          };
        },
      );

      const newViews = [...state.views]; // make a copy
      const newDataTables = [...state.dataTables]; // make a copy
      const newErrors = [...state.errors];
      const newChartConfigs = [...state.chartConfigs]; // make a copy
      // for each widget, see if there is content available for it
      state.views.map((existingView, index) => {
        const foundIndex = loadedViews.findIndex(
          (v) => v && existingView && v.series() === existingView.series(),
        );
        if (foundIndex === -1) {
          // no data available for this widget
          return;
        }
        const wc = widgetContents[foundIndex];
        const view = loadedViews[foundIndex];
        newViews[index] = view;
        newDataTables[index] = wc.dt;
        newErrors[index] = wc.error;
        newChartConfigs[index] = wc.chartConfig;
      });
      return {
        ...state,
        views: newViews,
        dataTables: newDataTables,
        chartConfigs: newChartConfigs,
        errors: newErrors,
        isSnapshot: action.payload.json._isScreenshotSnapshot,
        secondsBetweenRetry:
          action.payload.incrementRetryTime && viewsWithResults.length === 0
            ? Math.min(30, state.secondsBetweenRetry + 1)
            : 1, // reset to 1
        viewsAwaitingResults: viewsAwaitingResults.filter(
          (v1) => !viewsWithResults.some((v2) => v2.series() === v1.series()),
        ), // remove any that have been completed
      };
    }
    case MODAL_INTERACTION: {
      if (action.payload.modalType !== DASHBOARDS_VIEW) {
        return state;
      }
      return interaction(state, action.payload.props);
    }
    case PIN_REFRESHED: {
      return { ...state, publicPin: action.payload.pin };
    }
    case WIDGET_CONFIGURATION_UPDATED: {
      const widgetIdx = action.payload.widgetIndex;
      const newChartConfigs = [...state.chartConfigs];
      if (action.payload.chartConfig) {
        newChartConfigs[widgetIdx] = action.payload.chartConfig;
      }
      const current = state.chartWidgets[widgetIdx];
      const newWidget = DashboardChartWidget.create(
        current.series,
        current.key,
        current.x,
        current.y,
        current.width,
        current.height,
        current.displayType,
        action.payload.widgetTitle || current.widgetTitle,
        action.payload.chartConfig || current.chartSetting,
        current.metricColumns,
        current.dimensionColumns,
      );

      const newWidgets = updateArrayElement(
        state.chartWidgets,
        widgetIdx,
        newWidget,
      );
      return {
        ...state,
        chartConfigs: newChartConfigs,
        chartWidgets: newWidgets,
        unsavedChanges: true,
      };
    }
    default:
      return state;
  }
};
